import type { SideMenuPrefs } from './side-menu';

/** Interface preferences → Appearance → Theme (settings-general.md): Light (Pipedrive's default), Dark or System. */
export type Theme = 'light' | 'dark' | 'system';

/**
 * "Open details view after creating a new item" (Personal preferences → Interface, PP3) for the item types besides
 * Lead/deal, whose switch is `openLeadDealDetailsAfterCreate` (ADR-397).
 */
export interface OpenDetailsAfterCreate {
  person: boolean;
  organization: boolean;
  project: boolean;
}
export const THEMES: readonly Theme[] = ['light', 'dark', 'system'];

/**
 * Per-user interface preferences stored in `core.users.interface_prefs` (data-model.md). Only the keys
 * built so far. Defaults apply when a key was never set.
 */
export interface InterfacePrefs {
  /** "Show matches as I type" in the Add person duplicate popover (contacts §4.8 rule 48). On by default. */
  showDuplicateMatches: boolean;
  /**
   * "Show add activity modal after winning a deal" (Personal preferences → Interface, activities rule 25).
   * Only honored while the company allows regular users to disable the pop-up. Default (verify in trial): on.
   */
  showAfterWonActivityModal: boolean;
  /**
   * "Show hidden activities and notes in detail views" (activities rule 33, timeline › Hidden items).
   * Default (verify in trial): on.
   */
  showHiddenItemsInDetailViews: boolean;
  /**
   * "Automatically add "lead" and "deal" to lead/deal titles" (Personal preferences → Interface, leads rule 5).
   * Off by default (verified: the Add lead dialog's tip says the word wasn't added).
   */
  addLeadDealWordToTitles: boolean;
  /**
   * Item types whose suggested-filter card the user closed or applied (filters-and-views §3, contacts §5): it stays
   * away for them afterwards (interim, ADR-137). Empty by default.
   */
  dismissedSuggestedFilters: string[];
  /**
   * Interface preferences → Appearance → Theme (ADR-143). `null` until the user picks one: the browser keeps its
   * last choice (or Light, Pipedrive's default) and the web app copies it here once.
   */
  theme: Theme | null;
  /** Interface preferences → Navigation → "Keyboard shortcuts" (on by default, settings-general.md). */
  keyboardShortcuts: boolean;
  /** "Default landing page": a side-menu item id (e.g. `activities`); `null` opens the module default (Deals). */
  landingPage: string | null;
  /** The side menu customization (up to 10 items in the sidebar, the rest under More); `null` = default setup. */
  sideMenu: SideMenuPrefs | null;
  /**
   * Interface preferences → Appearance → "Show day and month views in the activities calendar" (ADR-195, BOLTcrm's own
   * option): adds the Day · Week · Month switch of ADR-175. Off by default, so the calendar is Pipedrive's week-only one.
   */
  calendarDayMonthViews: boolean;
  /**
   * "Open details view after creating a new item" for leads and deals (behaviour findings leads CV2, CV3, ADR-397):
   * adding a lead or a deal and converting either way open the new item when on. `null` until the user answers the
   * one-time question after their first add or conversion (Pipedrive asks once; its switch starts off).
   */
  openLeadDealDetailsAfterCreate: boolean | null;
  /**
   * "Open details view after creating a new item" for Person, Organization and Project (PP3, ADR-449); Lead/deal is
   * `openLeadDealDetailsAfterCreate` (ADR-397). Personal preferences shows the four as one tri-state group. Default
   * (verify in trial): off.
   */
  openDetailsAfterCreate: OpenDetailsAfterCreate;
  /** "Show celebratory animation after every won deal" (PP4, ADR-449). On by default (ticked in the trial). */
  celebrateWonDeals: boolean;
  /** Personal preferences → General → Phone (trial 2026-11-02): the user's own number, free text. Empty by default. */
  phone: string;
  /**
   * Personal preferences → Default currency (PP5, ADR-450): the currency new deals, leads and products start in for this
   * user. `null` uses the company's default currency (Company settings → Currencies).
   */
  defaultCurrency: string | null;
  /**
   * The tab the Filter popover opened on last (Favorites, Owners or Filters), kept per user across reloads (V277,
   * ADR-942). `null` until a tab was used: the popover then opens as before (Filters with a filter chosen, else Owners).
   */
  filterTab: 'favorites' | 'owners' | 'filters' | null;
  /**
   * Activities list → "…" → "Detail view" (ADR-1094, verified in the trial): a row opens the wide
   * panel with the day calendar and the linked-record tabs (`fullScreen`, the default) or the "Edit activity" dialog
   * (`modal`).
   */
  activityDetailView: ActivityDetailView;
}
export type ActivityDetailView = 'modal' | 'fullScreen';

export const DEFAULT_INTERFACE_PREFS: InterfacePrefs = {
  showDuplicateMatches: true,
  showAfterWonActivityModal: true,
  showHiddenItemsInDetailViews: true,
  addLeadDealWordToTitles: false,
  dismissedSuggestedFilters: [],
  theme: null,
  keyboardShortcuts: true,
  landingPage: null,
  sideMenu: null,
  calendarDayMonthViews: false,
  openLeadDealDetailsAfterCreate: null,
  openDetailsAfterCreate: { person: false, organization: false, project: false },
  celebrateWonDeals: true,
  phone: '',
  defaultCurrency: null,
  filterTab: null,
  activityDetailView: 'fullScreen',
};
